import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { GraduationCap, Play, CheckCircle2, Award, Clock, Lock } from 'lucide-react';
import { toThumbnailUrl } from '@/utils/drive';

const fmtExpiry = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return null; }
};

export default function Academy() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.getCourses();
      setCourses(r.data);
    } catch (e) { toast.error('Error al cargar cursos'); }
  };

  const renderProgress = (c) => {
    const p = c.user_progress;
    if (!p) return { label: 'Sin iniciar', color: 'bg-gray-100 text-gray-700' };
    if (p.status === 'completed') return { label: `Aprobado · ${p.score}%`, color: 'bg-green-50 text-green-700' };
    return { label: 'En progreso', color: 'bg-amber-50 text-amber-700' };
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="academy-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
            <GraduationCap className="h-7 w-7 text-[#DC143C]" />
            Mi Academia
          </h1>
          <p className="text-gray-600 mt-1">Cursos disponibles para ti</p>
        </div>

        {courses.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            <GraduationCap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No hay cursos disponibles en este momento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => {
              const prog = renderProgress(c);
              const completed = c.user_progress?.status === 'completed';
              const isExpired = !!c.assignment_is_expired && !completed;
              const daysLeft = c.assignment_days_left;
              const expiresFmt = fmtExpiry(c.assignment_expires_at);
              return (
                <div key={c.id} className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow ${isExpired ? 'border-gray-300 opacity-75' : 'border-gray-200'}`} data-testid="academy-course-card">
                  <div className="relative">
                    {c.thumbnail_url ? (
                      <img src={toThumbnailUrl(c.thumbnail_url, 480)} alt={c.title} className={`w-full h-40 object-cover bg-gray-100 ${isExpired ? 'grayscale' : ''}`} onError={(e) => { e.target.parentElement.classList.add('thumb-failed'); e.target.style.display = 'none'; }} />
                    ) : (
                      <div className={`w-full h-40 flex items-center justify-center ${isExpired ? 'bg-gradient-to-br from-gray-400 to-gray-500' : 'bg-gradient-to-br from-[#DC143C] to-[#B91C1C]'}`}>
                        <GraduationCap className="h-16 w-16 text-white/40" />
                      </div>
                    )}
                    {isExpired && (
                      <div className="absolute top-2 left-2 bg-gray-900/90 text-white text-xs font-bold uppercase tracking-wide px-2 py-1 rounded-full flex items-center gap-1" data-testid="expired-badge">
                        <Lock className="h-3 w-3" /> Periodo expirado
                      </div>
                    )}
                    {!isExpired && !completed && typeof daysLeft === 'number' && (
                      <div className={`absolute top-2 left-2 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1 ${daysLeft <= 1 ? 'bg-red-600' : daysLeft <= 3 ? 'bg-amber-600' : 'bg-blue-600'}`} data-testid="days-left-badge">
                        <Clock className="h-3 w-3" /> {daysLeft === 0 ? 'Expira hoy' : daysLeft === 1 ? '1 día' : `${daysLeft} días`}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg font-bold text-gray-900 line-clamp-2 flex-1">{c.title}</h3>
                      {completed && <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{c.description}</p>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${prog.color}`}>{prog.label}</span>
                      {isExpired ? (
                        <span className="text-xs text-gray-500">Disponibilidad: {expiresFmt}</span>
                      ) : !completed && expiresFmt ? (
                        <span className="text-xs text-gray-500">Vence: {expiresFmt}</span>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate(`/academia/curso/${c.id}`)}
                        data-testid="open-course-button"
                        disabled={isExpired}
                        title={isExpired ? 'El periodo disponible expiró. Contacta a tu administrador para una extensión.' : undefined}
                        className={`flex-1 flex items-center justify-center gap-1 ${isExpired ? 'bg-gray-300 text-gray-500 cursor-not-allowed hover:bg-gray-300' : 'bg-[#DC143C] hover:bg-[#B91C1C] text-white'}`}
                      >
                        {isExpired ? <Lock className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        {isExpired ? 'No disponible' : completed ? 'Repasar' : c.user_progress ? 'Continuar' : 'Iniciar'}
                      </Button>
                      {completed && (
                        <Button variant="outline" onClick={() => navigate(`/academia/certificado/${c.id}`)} data-testid="open-certificate-button">
                          <Award className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
